import { FC, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useAuth, useStore } from "../../../hooks";
import { UpdateUserProfileApi } from "../../../services";
import { validateDateOfBirth, validateEmail } from "../../../helpers";
import { CustomerModel } from "../../../models";
import { BiEnvelope, BiLockOpen, BiUser } from "react-icons/bi";
import PhonePrefixSelect, {
  COUNTRY_CODES,
} from "../../cart/_modals/PhonePrefixSelect";
import { Toast } from "../../Toast";

type FormFields = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  customer: CustomerModel;
  handleClose: any;
};

const minAge = import.meta.env.VITE_APP_MIN_AGE;

const EditProfileModal: FC<Props> = ({ show, customer, handleClose }) => {
  const { storeId } = useStore();
  const { setCustomer } = useAuth();

  const [_, setValidated] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormFields>({
    first_name: customer?.first_name || "",
    last_name: customer?.last_name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    dob: customer?.customer_dob || "",
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const prevValue = formData[name as keyof typeof formData];

    if (["first_name", "last_name"].includes(name))
      value = value.replace(/[^a-zA-Z\s]/g, "");
    else if (["phone"].includes(name)) value = value.replace(/[^0-9]/g, "");

    const meaningfulChange = value.trim() !== prevValue?.toString().trim();
    if (meaningfulChange) {
      setErrors((prev) => ({ ...prev, [name]: "" })); // Clear the error when user types
    }

    setFormData((prev) => ({ ...prev, [name]: value.trim() }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    setValidated(false);

    if (!formData.first_name) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email address is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length != 10) {
      newErrors.phone = "Phone number must be of 10 digits";
    }

    const dobError = validateDateOfBirth(formData.dob);
    if (dobError) {
      newErrors.dob = dobError;
    }

    setErrors(newErrors);
    setValidated(true);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) return false;

    try {
      setIsSubmitting(true);
      const { status, message } = await UpdateUserProfileApi({
        store_id: storeId,
        customer: { ...formData, id: customer.id },
      });

      if (status) {
        setCustomer({
          ...customer,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          customer_dob: formData.dob,
        });

        Toast.info(message);
        handleClose();
      } else Toast.error(message);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      className="login modal-width modal-mobile-bottom"
      show={show}
      onHide={handleClose}
      fullscreen="sm-down"
      centered
    >
      <Form method="POST" onSubmit={handleSubmit}>
        <Modal.Header closeButton className="login-modal-header">
          <Modal.Title className="login-modal-title">My Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body className="login-modal-body pt-0">
          <Row className="login_main">
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label className="login-label">
                  First Name <span className="text-danger">*</span>
                </Form.Label>
                <div className="address_icon">
                  <span>
                    <BiUser size={20} />
                  </span>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    isInvalid={!!errors.first_name}
                    required
                    className="login-input"
                  />
                </div>
                {errors.first_name && (
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {errors.first_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label className="login-label">Last Name</Form.Label>
                <div className="address_icon">
                  <span>
                    <BiUser size={20} />
                  </span>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    isInvalid={!!errors.last_name}
                  />
                </div>
                {errors.last_name && (
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {errors.last_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col sm={12} className="mb-3">
              <Form.Label className="login-label">
                Phone Number <span className="text-danger">*</span>
              </Form.Label>
              <div className="phone_field_wrapper">
                {/* COUNTRY PREFIX */}
                <PhonePrefixSelect value={COUNTRY_CODES[0]} />

                {/* PHONE INPUT */}
                <Form.Control
                  type="tel"
                  placeholder="(201) 555-0123"
                  name="phone"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  required
                  className="login-input phone-control"
                />
                <div className="input-helper-text">
                  We’ll use this to keep you updated about your order.
                </div>
              </div>

              {errors.phone && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              )}
            </Col>
            <Col sm={12}>
              <Form.Group className="mb-3">
                <Form.Label className="login-label">
                  Email Address <span className="text-danger">*</span>
                </Form.Label>
                <div className="address_icon">
                  <span>
                    <BiEnvelope size={20} />
                  </span>
                  <Form.Control
                    type="email"
                    name="email"
                    defaultValue={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    required
                    className="login-input"
                  />
                </div>
                {errors.email && (
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col sm={12}>
              <Form.Group className="mb-2">
                <Form.Label className="login-label">Date of Birth</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  max={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - minAge),
                    )
                      .toISOString()
                      .split("T")[0]
                  }
                  defaultValue={formData.dob}
                  onChange={handleChange}
                  isInvalid={!!errors.dob}
                  className="login-input"
                />
                {errors.dob && (
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {errors.dob}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="w-100 login_btn"
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner animation="border" variant="light" size="sm" />
            ) : (
              `Update`
            )}
          </Button>
          <div className="d-flex justify-content-center align-items-center w-100 modal-bottom-text">
            <BiLockOpen size={22} />
            We keep your information secure and private
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditProfileModal;
